import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import type { ChangeRequest, OrderWorkflow } from '@nucleus-commerce/api-client/admin';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useStageFormError } from '../hooks/use-stage-form-error';
import { useCreateChangeRequest, useResolveChangeRequest } from '../hooks/use-workflow-mutations';
import {
  changeRequestSchema,
  resolveChangeSchema,
  type ChangeRequestForm,
  type ResolveChangeForm,
} from '../schemas/stage-forms';

import { ActionDialog } from './action-dialog';

const NO_STAGE = '__none__';
const CREATE_FIELDS = new Set(['message', 'extraCost', 'stageKey']);
const RESOLVE_FIELDS = new Set(['note', 'extraCost']);
const moneyField = (field: string) => (field.startsWith('extraCost.') ? 'extraCost' : field);

export interface ChangeRequestDialogProps {
  storeId: string;
  orderId: string;
  currency: string;
  workflow: OrderWorkflow;
  onOpenChange: (open: boolean) => void;
}

/** A change the customer asked for by phone or email; the extra cost is recorded, never charged. */
export function ChangeRequestDialog({
  storeId,
  orderId,
  currency,
  workflow,
  onOpenChange,
}: ChangeRequestDialogProps) {
  const { t } = useTranslation('production');
  const create = useCreateChangeRequest(storeId, orderId);
  const form = useForm<ChangeRequestForm>({
    resolver: useI18nResolver(changeRequestSchema),
    defaultValues: { message: '', extraCost: null, stageKey: workflow.currentStageKey ?? NO_STAGE },
  });
  const onError = useStageFormError(form.setError, {
    knownFields: CREATE_FIELDS,
    mapField: moneyField,
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await create.mutateAsync({
        message: values.message,
        extraCost: values.extraCost && values.extraCost.amount > 0 ? values.extraCost : null,
        stageKey: values.stageKey === NO_STAGE ? null : values.stageKey,
      });
      onOpenChange(false);
    } catch (error) {
      onError(error);
    }
  });

  return (
    <Form {...form}>
      <ActionDialog
        open
        onOpenChange={onOpenChange}
        title={t('changes.addTitle')}
        description={t('changes.addDescription')}
        rootError={form.formState.errors.root?.server?.message}
        isPending={create.isPending}
        submitLabel={t('changes.addSubmit')}
        submittingLabel={t('changes.submitting')}
        onSubmit={submit}
      >
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('changes.message')}</FormLabel>
              <FormControl>
                <Textarea {...field} rows={3} maxLength={2000} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="extraCost"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('changes.extraCostLabel')}</FormLabel>
              <FormControl>
                <MoneyInput
                  value={field.value}
                  onChange={field.onChange}
                  currency={currency}
                  className="sm:max-w-48"
                />
              </FormControl>
              <FormDescription>{t('changes.extraCostHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="stageKey"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('changes.stageLabel')}</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={NO_STAGE}>{t('changes.noStage')}</SelectItem>
                  {workflow.stages.map((stage) => (
                    <SelectItem key={stage.key} value={stage.key}>
                      {stage.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </ActionDialog>
    </Form>
  );
}

export interface ResolveChangeDialogProps {
  storeId: string;
  orderId: string;
  currency: string;
  request: ChangeRequest;
  onOpenChange: (open: boolean) => void;
}

export function ResolveChangeDialog({
  storeId,
  orderId,
  currency,
  request,
  onOpenChange,
}: ResolveChangeDialogProps) {
  const { t } = useTranslation('production');
  const resolve = useResolveChangeRequest(storeId, orderId);
  const form = useForm<ResolveChangeForm>({
    resolver: useI18nResolver(resolveChangeSchema),
    defaultValues: { note: '', extraCost: request.extraCost },
  });
  const onError = useStageFormError(form.setError, {
    knownFields: RESOLVE_FIELDS,
    mapField: moneyField,
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    const changed = (values.extraCost?.amount ?? null) !== (request.extraCost?.amount ?? null);
    try {
      await resolve.mutateAsync({
        requestId: request.id,
        body: {
          note: values.note,
          ...(changed
            ? {
                extraCost:
                  values.extraCost && values.extraCost.amount > 0 ? values.extraCost : null,
              }
            : {}),
        },
      });
      onOpenChange(false);
    } catch (error) {
      onError(error);
    }
  });

  return (
    <Form {...form}>
      <ActionDialog
        open
        onOpenChange={onOpenChange}
        title={t('changes.resolveTitle')}
        description={request.message}
        rootError={form.formState.errors.root?.server?.message}
        isPending={resolve.isPending}
        submitLabel={t('changes.resolveSubmit')}
        submittingLabel={t('changes.submitting')}
        onSubmit={submit}
      >
        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('changes.note')}</FormLabel>
              <FormControl>
                <Textarea {...field} rows={3} maxLength={2000} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="extraCost"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('changes.extraCostLabel')}</FormLabel>
              <FormControl>
                <MoneyInput
                  value={field.value}
                  onChange={field.onChange}
                  currency={currency}
                  className="sm:max-w-48"
                />
              </FormControl>
              <FormDescription>{t('changes.keepCost')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </ActionDialog>
    </Form>
  );
}
